"use client";

import { CloseOutlined } from "@ant-design/icons";
import { Button, Form, Input } from "antd";
import TextArea from "antd/es/input/TextArea";
import React, { useState } from "react";
import Loader from "../../loader";
import { ContactUs } from "@/api/contact-us";
import { useNotification } from "@/app/hooks/useNotification";

const ContactUsForm = () => {
  type FieldType = {
    name: string;
    email: string;
    subject: string;
    message: string;
  };

  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const { openNotification } = useNotification();

  const onFinish = async (values: FieldType) => {
    setIsLoading(true);
    try {
      await ContactUs({
        description: values.message,
        email: values.email,
        full_name: values.name,
        subject: values.subject,
      });
      openNotification({
        type: "success",
        message: "پیام با موفقیت ثبت گردید !",
      });
      form.resetFields();
    } catch (err) {
      openNotification({
        type: "error",
        message: "لطفا مجدد تلاش کنید.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Form
        form={form}
        name="basic"
        labelCol={{ span: 8 }}
        initialValues={{ remember: true }}
        layout="vertical"
        onFinish={onFinish}
        autoComplete="off"
        className="w-full flex flex-col h-[23rem] sm:h-96 md:h-[25rem] justify-between md:w-1/2"
      >
        <div className="flex flex-col gap-12 lg:gap-13">
          <div className="flex w-full items-start justify-center gap-2">
            <Form.Item<FieldType>
              label={<span className="text-xs">اسم</span>}
              name="name"
              className="flex-1 !mb-0 !h-8 sm:!h-10"
              rules={[
                { required: true, message: "لطفا نام خود را وارد نمایید." },
                {
                  validator(_, value) {
                    if (!value || (value.length >= 4 && value.length <= 90)) {
                      return Promise.resolve();
                    }
                    return Promise.reject(
                      new Error("نام شما باید بین 4 تا 90 کاراکتر باشد!")
                    );
                  },
                },
              ]}
            >
              <Input
                className="!rounded-md !h-8 sm:!h-10"
                allowClear={{
                  clearIcon: (
                    <CloseOutlined
                      size={16}
                      style={{ color: "var(--color-grey)" }}
                    />
                  ),
                }}
              />
            </Form.Item>
            <Form.Item<FieldType>
              label={<span className="text-xs">ایمیل</span>}
              name="email"
              className="flex-1 !h-8 sm:!h-10 !mb-0"
              rules={[
                {
                  required: true,
                  message: "لطفا ایمیل خود را وارد نمایید."
                },
                {
                  type: 'email',
                  message: "ایمیل وارد شده معتبر نیست!"
                },
              ]}
            >
              <Input
                className="!rounded-md !h-8 sm:!h-10"
                allowClear={{
                  clearIcon: (
                    <CloseOutlined
                      size={16}
                      style={{ color: "var(--color-primary)" }}
                    />
                  ),
                }}
              />
            </Form.Item>
          </div>
          <Form.Item<FieldType>
            label={<span className="text-xs">موضوع</span>}
            name="subject"
            className="w-full !h-8 sm:!h-10 !mb-0"
            rules={[
              {
                required: true,
                message: "لطفا موضوع پیام خود را وارد نمایید.",
              },
              {
                validator(_, value) {
                  if (!value || (value.length >= 4 && value.length <= 150)) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error("موضوع پیام شما باید بین 4 تا 150 کاراکتر باشد!")
                  );
                },
              },
            ]}
          >
            <Input
              className="!rounded-md !h-8 sm:!h-10"
              allowClear={{
                clearIcon: (
                  <CloseOutlined
                    size={16}
                    style={{ color: "var(--color-primary)" }}
                  />
                ),
              }}
            />
          </Form.Item>
          <Form.Item<FieldType>
            label={<span className="text-xs">متن پیام</span>}
            name="message"
            className="w-full !h-24 !mb-0"
            rules={[
              { required: true, message: "لطفا متن پیام خود را وارد نمایید." },
              {
                validator(_, value) {
                  if (!value || (value.length >= 10 && value.length <= 300)) {
                    return Promise.resolve();
                  }
                  if (value && value.length < 10) {
                    return Promise.reject(
                      new Error("متن پیام شما باید حداقل 10 کاراکتر باشد!")
                    );
                  }
                  return Promise.reject(
                    new Error("متن پیام شما نباید بیشتر از 300 کاراکتر باشد!")
                  );
                },
              },
            ]}
          >
            <TextArea
              showCount
              className="!h-24 !resize-none"
              maxLength={300}
              onPressEnter={() => form.submit()}
            />
          </Form.Item>
        </div>
        <Form.Item className="pb-0 !mb-0 w-full">
          <Button
            type="primary"
            htmlType="submit"
            className="w-full !h-10 xl:!h-12 !text-base !font-semibold"
            disabled={isLoading}
          >
            ارسال پیام
            {isLoading ? <Loader /> : ""}
          </Button>
        </Form.Item>
      </Form>
    </>
  );
};

export default ContactUsForm;
